import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  DataUpdateRequestKpis,
  DataUpdateRequestListFilters,
  DataUpdateRequestSummary,
  RequestEstado,
} from '../../../interfaces/data-update-request.interface';
import { DataUpdateRequestService } from '../../../services/data-update-request.service';
import { CreateDataUpdateRequestComponent } from '../create-data-update-request/create-data-update-request.component';
import { parseUsuarioId } from '../../data-update-request/data-update-request.utils';

const EMPTY_KPIS: DataUpdateRequestKpis = {
  pendientes: 0,
  vencidas: 0,
  esperandoRevision: 0,
  rechazadas: 0,
  completadas: 0,
  reportadas: 0,
};

/**
 * Vista principal del operador para "Solicitudes de actualización".
 * Combina KPIs arriba + tabla paginada abajo en una sola pantalla
 * (más simple que dos rutas separadas — el operador ve todo de un vistazo).
 */
@Component({
  selector: 'app-data-update-requests-list',
  templateUrl: './data-update-requests-list.component.html',
  styleUrls: ['./data-update-requests-list.component.sass'],
})
export class DataUpdateRequestsListComponent implements OnInit {
  kpis: DataUpdateRequestKpis = EMPTY_KPIS;
  rows: DataUpdateRequestSummary[] = [];
  total: number = 0;
  page: number = 1;
  limit: number = 20;
  totalPages: number = 0;

  loading: boolean = false;
  errorMessage: string | null = null;

  // Filtros
  /**
   * Al abrir el listado el operador casi siempre va a revisar las
   * solicitudes pendientes de acción; arrancamos filtrando por
   * `PENDING` en lugar de "todas" para que la vista sea útil de arranque.
   * El operador puede desactivar el filtro (opción "Todos") para ver
   * el histórico completo.
   */
  filterEstado: RequestEstado | '' = 'PENDING';
  filterVencida: boolean | null = null;
  filterRisk: boolean | null = null;
  filterQ: string = '';

  /**
   * Filtro "sólo con ítems por revisar": muestra únicamente las
   * solicitudes con ítems SUBMITTED esperando aprobación del operador.
   * Se resuelve server-side con el query param `esperandoRevision=true`
   * (ver `buildFilters`), así que respeta la paginación normal.
   */
  filterEnRevision: boolean = false;

  constructor(
    private service: DataUpdateRequestService,
    private router: Router,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.loadAll();
  }

  async loadAll(): Promise<void> {
    this.loading = true;
    this.errorMessage = null;
    try {
      const [kpis, list] = await Promise.all([
        this.service.getKpis().catch(() => EMPTY_KPIS),
        this.service.listRequests(this.buildFilters()),
      ]);
      this.kpis = kpis;
      this.rows = this.sortByStatusPriority(list.data);
      this.total = list.meta.total;
      this.totalPages = list.meta.totalPages;
    } catch (err: any) {
      this.errorMessage =
        err?.error?.message ||
        err?.message ||
        'No se pudo cargar el listado.';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Ordena las solicitudes por prioridad de atención:
   *   1. PENDING           → requieren acción del operador (arriba)
   *   2. COMPLETED         → resueltas satisfactoriamente
   *   3. resto             → EXPIRED / CANCELLED / CLOSED_MAX_REJECTS
   *
   * Aplicamos client-side sobre la página devuelta por el backend. Si
   * a futuro el backend expone un `sortBy` propio, este orden puede
   * quedar como fallback sobre lo que llegue.
   */
  private sortByStatusPriority(
    rows: DataUpdateRequestSummary[],
  ): DataUpdateRequestSummary[] {
    const rank: Record<string, number> = {
      PENDING: 0,
      COMPLETED: 1,
    };
    return [...rows].sort((a, b) => {
      // Las solicitudes con ítems por revisar van primero: son las que
      // requieren acción inmediata del operador (aprobar/rechazar).
      const ar = this.hasItemsPendingReview(a) ? 0 : 1;
      const br = this.hasItemsPendingReview(b) ? 0 : 1;
      if (ar !== br) return ar - br;
      const ra = rank[a.estado] ?? 2;
      const rb = rank[b.estado] ?? 2;
      return ra - rb;
    });
  }

  buildFilters(): DataUpdateRequestListFilters {
    const f: DataUpdateRequestListFilters = {
      page: this.page,
      limit: this.limit,
    };
    if (this.filterEstado) f.estado = this.filterEstado;
    if (this.filterVencida !== null) f.vencida = this.filterVencida;
    if (this.filterRisk !== null) f.riskReported = this.filterRisk;
    // Búsqueda: un input puramente numérico se interpreta como
    // `usuarioId` (búsqueda exacta por ID de usuario) — el backend no
    // matchea el usuarioId vía `q`. Cualquier otra cosa va como `q`
    // (búsqueda libre por nombre / numeroId / email). Mismo criterio que
    // el buscador de creación de solicitudes.
    const search = this.filterQ.trim();
    if (search) {
      const usuarioId = parseUsuarioId(search);
      if (usuarioId !== null) f.usuarioId = usuarioId;
      else f.q = search;
    }
    // Cola de revisión: el backend filtra server-side con este flag y
    // sigue paginando normal. Solo lo mandamos cuando está activo.
    if (this.filterEnRevision) f.esperandoRevision = true;
    return f;
  }

  /** Activa/desactiva el filtro "sólo con ítems por revisar". */
  toggleEnRevision(): void {
    this.filterEnRevision = !this.filterEnRevision;
    this.applyFilters();
  }

  applyFilters(): void {
    this.page = 1;
    this.loadAll();
  }

  clearFilters(): void {
    // "Limpiar filtros" muestra TODAS las solicitudes — el default
    // `PENDING` sólo aplica al abrir la vista (`ngOnInit`); cuando el
    // operador clickea limpiar es porque quiere ver el histórico
    // completo sin filtros.
    this.filterEstado = '';
    this.filterVencida = null;
    this.filterRisk = null;
    this.filterQ = '';
    this.filterEnRevision = false;
    this.applyFilters();
  }

  goToDetail(row: DataUpdateRequestSummary): void {
    this.router.navigate(['/wallet/admin/data-update-requests', row.id]);
  }

  openCreate(): void {
    const ref = this.dialog.open(CreateDataUpdateRequestComponent, {
      maxWidth: '720px',
      minWidth: '300px',
      width: '90vw',
      maxHeight: '90vh',
      autoFocus: false,
    });
    ref.afterClosed().subscribe((result) => {
      if (result?.created) this.loadAll();
    });
  }

  /** Lleva al operador a la página de creación masiva (matriz). */
  goToBulkCreate(): void {
    this.router.navigate(['/wallet/admin/data-update-requests/bulk/new']);
  }

  async cancelRequest(row: DataUpdateRequestSummary): Promise<void> {
    if (!confirm(`¿Cancelar la solicitud de ${row.usuarioNombre}?`)) return;
    try {
      await this.service.cancel(row.id);
      await this.loadAll();
    } catch (err: any) {
      alert(err?.error?.message || err?.message || 'No se pudo cancelar.');
    }
  }

  nextPage(): void {
    if (this.page >= this.totalPages) return;
    this.page += 1;
    this.loadAll();
  }

  prevPage(): void {
    if (this.page <= 1) return;
    this.page -= 1;
    this.loadAll();
  }

  // ── Helpers de presentación ────────────────────────────────────────

  /** Cantidad de items pendientes — acepta shape en español o inglés. */
  pendientesOf(row: DataUpdateRequestSummary): number {
    return row.itemsPendientes ?? row.itemsPending ?? 0;
  }

  /** Cantidad de items aprobados. */
  aprobadosOf(row: DataUpdateRequestSummary): number {
    return row.itemsAprobados ?? 0;
  }

  /**
   * Ítems que el cliente aún NO suministró (PENDING). Usa el campo
   * autoritativo del backend `itemsSinEnviar`; si no viene (backend
   * viejo) cae a `itemsPendientes` para no romper.
   */
  sinEnviarOf(row: DataUpdateRequestSummary): number {
    if (typeof row.itemsSinEnviar === 'number') return row.itemsSinEnviar;
    return this.pendientesOf(row);
  }

  /**
   * Ítems en revisión (SUBMITTED): suministrados por el cliente y
   * esperando que el operador los apruebe/rechace. Usa el campo
   * autoritativo `itemsEsperandoRevision`; si no viene, lo deriva como
   * `total − pendientes − aprobados` (fallback backend viejo).
   */
  enRevisionCount(row: DataUpdateRequestSummary): number {
    if (typeof row.itemsEsperandoRevision === 'number') {
      return row.itemsEsperandoRevision;
    }
    return Math.max(
      0,
      row.itemsTotal - this.pendientesOf(row) - this.aprobadosOf(row),
    );
  }

  /** Ítems rechazados (REJECTED). */
  rechazadosOf(row: DataUpdateRequestSummary): number {
    return row.itemsRechazados ?? 0;
  }

  /**
   * `true` si la solicitud tiene ítems esperando revisión del operador.
   * Gatilla el resaltado amarillo y el filtro "sólo por revisar". Usa la
   * señal autoritativa `requiereRevision`; si no viene, cae al conteo.
   */
  hasItemsPendingReview(row: DataUpdateRequestSummary): boolean {
    if (typeof row.requiereRevision === 'boolean') return row.requiereRevision;
    return row.estado === 'PENDING' && this.enRevisionCount(row) > 0;
  }

  /**
   * Identificador visible del creador de la solicitud. El backend ahora
   * expone `createdByEmail` directamente — mostramos el email como
   * está. Si por algún motivo llega el legacy `createdByCognitoId`
   * (endpoint no migrado), mostramos un ID corto en fallback.
   */
  createdByLabel(row: DataUpdateRequestSummary): string {
    if (row.createdByEmail) return row.createdByEmail;
    if (row.createdByCognitoId) {
      return `#${row.createdByCognitoId.slice(0, 8)}`;
    }
    return '—';
  }

  /**
   * Nombre visible del usuario. Si no vino (backend actual no lo
   * incluye), mostramos `Usuario #<id>` como fallback trazable.
   */
  usuarioLabel(row: DataUpdateRequestSummary): string {
    if (row.usuarioNombre?.trim()) return row.usuarioNombre;
    return `Usuario #${row.usuarioId}`;
  }

  numeroIdLabel(row: DataUpdateRequestSummary): string {
    return row.numeroId || '—';
  }

  /**
   * Estado visual efectivo. Cuando el backend dice `PENDING` pero
   * `itemsPendientes === 0`, significa que el cliente ya envió todo y
   * la solicitud está esperando revisión del operador — lo mostramos
   * como "En revisión" en azul para diferenciarlo del PENDING inicial
   * (cliente no empezó).
   */
  private effectiveEstado(row: DataUpdateRequestSummary): string {
    // "En revisión" (azul): la solicitud está PENDING, tiene ítems
    // esperando decisión del operador y NO queda nada por hacer del lado
    // del cliente (ni sin enviar ni rechazados por reenviar). Las mixtas
    // (algo por revisar + algo que el cliente aún debe cargar) se dejan
    // como "Pendiente" pero con fila resaltada y badge "por revisar".
    if (
      row.estado === 'PENDING' &&
      this.hasItemsPendingReview(row) &&
      this.sinEnviarOf(row) === 0 &&
      this.rechazadosOf(row) === 0
    ) {
      return 'IN_REVIEW';
    }
    return row.estado;
  }

  estadoColor(row: DataUpdateRequestSummary): string {
    const estado = this.effectiveEstado(row);
    switch (estado) {
      case 'PENDING':
        return '#f59e0b';
      case 'IN_REVIEW':
        return '#3b82f6';
      case 'COMPLETED':
        return '#16a34a';
      case 'EXPIRED':
        return '#dc2626';
      case 'CANCELLED':
        return '#6b7280';
      case 'CLOSED_MAX_REJECTS':
        return '#7f1d1d';
      default:
        return '#6b7280';
    }
  }

  estadoLabel(row: DataUpdateRequestSummary): string {
    const estado = this.effectiveEstado(row);
    switch (estado) {
      case 'PENDING':
        return 'Pendiente';
      case 'IN_REVIEW':
        return 'En revisión';
      case 'COMPLETED':
        return 'Completada';
      case 'EXPIRED':
        return 'Vencida';
      case 'CANCELLED':
        return 'Cancelada';
      case 'CLOSED_MAX_REJECTS':
        return 'Cerrada (máx reintentos)';
      default:
        return String(estado);
    }
  }

  /**
   * Estados terminales — no requieren tracking de aging ni items
   * pendientes. La columna Aging muestra `—` en gris para ellos.
   */
  private isTerminal(estado: RequestEstado): boolean {
    return estado === 'COMPLETED' || estado === 'CANCELLED';
  }

  /**
   * Etiqueta compacta para la columna "Items". Formato "aprobados/total"
   * — indicador de progreso positivo. Los pendientes se muestran como
   * badge separado (ver template).
   */
  itemsCountLabel(row: DataUpdateRequestSummary): string {
    return `${this.aprobadosOf(row)}/${row.itemsTotal}`;
  }

  /**
   * Días de aging siguiendo la convención del backend: mide
   * `now - deadlineAt` en días completos.
   *
   *   > 0  → vencida hace N días.
   *   = 0  → vence hoy.
   *   < 0  → quedan |N| días.
   *
   * Preferimos el `agingDias`/`agingDays` que devuelve el backend
   * (evita drift de zona horaria); si no viene, lo derivamos del
   * `deadlineAt`. `NaN` y valores inválidos caen a `0` para que la UI
   * no muestre "undefined días".
   */
  private computeAgingDays(row: DataUpdateRequestSummary): number {
    const fromBackend = row.agingDias ?? row.agingDays;
    if (typeof fromBackend === 'number' && Number.isFinite(fromBackend)) {
      return fromBackend;
    }
    if (!row.deadlineAt) return 0;
    const target = new Date(row.deadlineAt).getTime();
    if (Number.isNaN(target)) return 0;
    const diffMs = Date.now() - target;
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  }

  /**
   * `true` si la solicitud está vencida. Usamos el flag explícito
   * `vencida` del backend cuando viene; si no, caemos al cálculo por
   * signo del aging.
   */
  private isOverdue(row: DataUpdateRequestSummary): boolean {
    if (typeof row.vencida === 'boolean') return row.vencida;
    return this.computeAgingDays(row) > 0;
  }

  agingLabel(row: DataUpdateRequestSummary): string {
    if (this.isTerminal(row.estado)) return '—';
    const days = this.computeAgingDays(row);
    if (this.isOverdue(row)) {
      return `Vencida ${Math.abs(days)}d`;
    }
    const remaining = Math.abs(days);
    if (remaining === 0) return 'Vence hoy';
    if (remaining === 1) return '1 día';
    return `${remaining} días`;
  }

  agingClass(row: DataUpdateRequestSummary): string {
    if (this.isTerminal(row.estado)) return 'aging--muted';
    if (this.isOverdue(row)) return 'aging--expired';
    const remaining = Math.abs(this.computeAgingDays(row));
    if (remaining <= 2) return 'aging--urgent';
    return 'aging--ok';
  }
}
